import React from 'react';
import { Trans } from 'react-i18next';
import ThemeConstants from '../../config/theme/theme_constants';
import { useTranslation } from '../../i18n/useTranslation';
import DocumentBackButton from './document_back_button';

const theme = ThemeConstants();

const EthicalCharter: React.FC = () => {
  return (
    <div className='flex flex-col min-h-dvh w-full relative' style={{ background: `linear-gradient(90deg, ${theme.UvibesPinkGradient} 0%, ${theme.UvibesOrangeGradient} 75%)` }}>
      <DocumentBackButton />
      <Header />
      <div className="rounded-t-[30px] bg-white px-4 py-8 w-full space-y-12">
        <Preambule />
        <ObjectifsCharte />
        <SolutionSecurisee />
        <BienEtreUtilisateurs />
        <SolutionInclusive />
        <ProtectionDonnees />
        <CollecteDonnees />
        <EnvironnementResponsable />
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="text-white p-12 text-center">
      <div className="space-y-6">
        <div className="text-sm font-medium tracking-wide uppercase">
          {t('ethics.date')}
        </div>
        <h1 className="text-4xl font-bold leading-tight">
          {t('ethics.title')}
        </h1>
        <div className="w-24 h-1 bg-white mx-auto rounded-full"></div>
      </div>
    </div>
  );
};

const Preambule: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="1" title={t('ethics.preamble.title')} />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>
          <Trans
            i18nKey="ethics.preamble.p1"
            components={{ brand: <span className="font-semibold" style={{ color: theme.UvibesPink }} /> }}
          />
        </p>
        <p>
          {t('ethics.preamble.p2')}
        </p>
        <p>
          {t('ethics.preamble.p3')}
        </p>
      </div>
    </section>
  );
};

const ObjectifsCharte: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="2" title={t('ethics.objectives.title')} />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>{t('ethics.objectives.intro')}</p>
        <ul className="space-y-3 ml-4">
          <li className="flex items-start">
            <span className="w-2 h-2 rounded-full mt-2 mr-3 shrink-0" style={{ backgroundColor: theme.UvibesPink }}></span>
            <span>{t('ethics.objectives.item_1')}</span>
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 rounded-full mt-2 mr-3 shrink-0" style={{ backgroundColor: theme.UvibesPink }}></span>
            <span>{t('ethics.objectives.item_2')}</span>
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 rounded-full mt-2 mr-3 shrink-0" style={{ backgroundColor: theme.UvibesPink }}></span>
            <span>{t('ethics.objectives.item_3')}</span>
          </li>
        </ul>
      </div>
    </section>
  );
};

const SolutionSecurisee: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="3" title={t('ethics.secure.title')} />

      <SubSection
        number="3.1"
        title={t('ethics.secure.trust.title')}
        content={t('ethics.secure.trust.content')}
      />

      <SubSection
        number="3.2"
        title={t('ethics.secure.freedom.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.secure.freedom.p1')}</p>
            <p>{t('ethics.secure.freedom.p2')}</p>
            <p>{t('ethics.secure.freedom.p3')}</p>
            <p>{t('ethics.secure.freedom.p4')}</p>
          </div>
        }
      />

      <SubSection
        number="3.3"
        title={t('ethics.secure.features.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.secure.features.p1')}</p>
            <p>{t('ethics.secure.features.p2')}</p>
            <p>{t('ethics.secure.features.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="3.4"
        title={t('ethics.secure.team.title')}
        content={t('ethics.secure.team.content')}
      />
    </section>
  );
};

const BienEtreUtilisateurs: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="4" title={t('ethics.wellbeing.title')} />

      <SubSection
        number="4.1"
        title={t('ethics.wellbeing.relations.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.wellbeing.relations.p1')}</p>
            <p>{t('ethics.wellbeing.relations.p2')}</p>
            <p>{t('ethics.wellbeing.relations.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="4.2"
        title={t('ethics.wellbeing.monitoring.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.wellbeing.monitoring.p1')}</p>
            <p>{t('ethics.wellbeing.monitoring.p2')}</p>
          </div>
        }
      />

      <SubSection
        number="4.3"
        title={t('ethics.wellbeing.attention.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.wellbeing.attention.p1')}</p>
            <p>{t('ethics.wellbeing.attention.p2')}</p>
            <p>{t('ethics.wellbeing.attention.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="4.4"
        title={t('ethics.wellbeing.barometer.title')}
        content={t('ethics.wellbeing.barometer.content')}
      />
    </section>
  );
};

const SolutionInclusive: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="5" title={t('ethics.inclusive.title')} />

      <SubSection
        number="5.1"
        title={t('ethics.inclusive.fair.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.inclusive.fair.p1')}</p>
            <p>{t('ethics.inclusive.fair.p2')}</p>
            <p>{t('ethics.inclusive.fair.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="5.2"
        title={t('ethics.inclusive.accessibility.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.inclusive.accessibility.p1')}</p>
            <p>{t('ethics.inclusive.accessibility.p2')}</p>
            <p>{t('ethics.inclusive.accessibility.p3')}</p>
          </div>
        }
      />
    </section>
  );
};

const ProtectionDonnees: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="6" title={t('ethics.data_protection.title')} />

      <SubSection
        number="6.1"
        title={t('ethics.data_protection.confidentiality.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.data_protection.confidentiality.p1')}</p>
            <p>{t('ethics.data_protection.confidentiality.p2')}</p>
            <p>{t('ethics.data_protection.confidentiality.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="6.2"
        title={t('ethics.data_protection.anonymous.title')}
        content={t('ethics.data_protection.anonymous.content')}
      />

      <SubSection
        number="6.3"
        title={t('ethics.data_protection.training.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.data_protection.training.p1')}</p>
            <p>{t('ethics.data_protection.training.p2')}</p>
          </div>
        }
      />

      <SubSection
        number="6.4"
        title={t('ethics.data_protection.aggregated.title')}
        content={t('ethics.data_protection.aggregated.content')}
      />
    </section>
  );
};

const CollecteDonnees: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="7" title={t('ethics.data_collection.title')} />

      <SubSection
        number="7.1"
        title={t('ethics.data_collection.limited.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.data_collection.limited.p1')}</p>
            <p>{t('ethics.data_collection.limited.p2')}</p>
          </div>
        }
      />

      <SubSection
        number="7.2"
        title={t('ethics.data_collection.no_commercial.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.data_collection.no_commercial.p1')}</p>
            <p>{t('ethics.data_collection.no_commercial.p2')}</p>
          </div>
        }
      />

      <SubSection
        number="7.3"
        title={t('ethics.data_collection.gdpr.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.data_collection.gdpr.p1')}</p>
            <p>{t('ethics.data_collection.gdpr.p2')}</p>
            <ul className="ml-6 space-y-1">
              <li>• {t('ethics.data_collection.gdpr.provider_zoom')}</li>
              <li>• {t('ethics.data_collection.gdpr.provider_ovh')}</li>
            </ul>
          </div>
        }
      />
    </section>
  );
};

const EnvironnementResponsable: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle number="8" title={t('ethics.environment.title')} />

      <SubSection
        number="8.1"
        title={t('ethics.environment.commitment.title')}
        content={t('ethics.environment.commitment.content')}
      />

      <SubSection
        number="8.2"
        title={t('ethics.environment.architecture.title')}
        content={
          <div className="space-y-4">
            <p>{t('ethics.environment.architecture.p1')}</p>
            <p>{t('ethics.environment.architecture.p2')}</p>
            <p>{t('ethics.environment.architecture.p3')}</p>
          </div>
        }
      />

      <SubSection
        number="8.3"
        title={t('ethics.environment.media.title')}
        content={t('ethics.environment.media.content')}
      />
    </section>
  );
};

interface SectionTitleProps {
  number: string;
  title: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ number, title }) => {
  return (
    <div className="border-l-4 pl-4" style={{ borderColor: theme.UvibesPink }}>
      <h2 className="text-2xl font-bold text-gray-800 flex items-baseline gap-3">
        <span style={{ color: theme.UvibesPink }}>{number}.</span>
        <span>{title}</span>
      </h2>
    </div>
  );
};

interface SubSectionProps {
  number: string;
  title: string;
  content: React.ReactNode | string;
}

const SubSection: React.FC<SubSectionProps> = ({ number, title, content }) => {
  return (
    <div className="ml-6 space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 flex items-baseline gap-2">
        <span style={{ color: theme.UvibesPink }}>{number}</span>
        <span>{title}</span>
      </h3>
      <div className="text-gray-700 leading-relaxed ml-8">
        {typeof content === 'string' ? <p>{content}</p> : content}
      </div>
    </div>
  );
};

export default EthicalCharter;
